import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { findRecord } from 'umg-ember-backend/data/builders/umg-rest';
import { getRecordSettings } from 'umg-ember-backend/utils/get-record-settings';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

export default class HistoryService extends Service {
  // Services
  @service confirm;
  @service fetch;
  @service flashMessages;
  @service intl;
  @service recordLocks;
  @service recordRelationships;
  @service recordVariants;
  @service store;

  // Defaults
  @tracked showErrors = false;

  // Functions
  async confirmAction(action, model) {
    let scope = getRecordType(model) === 'mlp' ? 'mlp' : 'default';
    scope = `confirm.${action}.${scope}`;

    return this.confirm.ask(`${scope}.title`, `${scope}.message`);
  }

  async deleteModel(model) {
    await this.recordLocks.lockStatus(model);

    if (!this.recordLocks.isLocked(model)) {
      const isVariant = this.recordVariants.isVariant(model);
      const response = await model.destroyRecord();

      if (isVariant) {
        this.flashMessages.success('flash.deleteModel.variant.success');
      } else {
        this.flashMessages.success('flash.deleteModel.default.success');
      }

      return response;
    }
  }

  async publishModel(model) {
    const recordType = getRecordType(model);

    if (recordType === 'mlp') {
      let response = await this.fetch.post(`/mlps/publish/${model.id}`, {
        data: { mlp: model.serialize() },
      });

      this.store.push(this.store.normalize(recordType, response.mlp));
      this.flashMessages.success('flash.publishModel.mlp.success');

      await this.store.request(findRecord('mlp', response.mlp.publishedMlp));
    } else {
      await this.recordLocks.lockStatus(model);

      if (!this.recordLocks.isLocked(model)) {
        model.cmsState = 2;

        await model.save();

        this.flashMessages.success('flash.publishModel.default.success');
      }
    }

    return model;
  }

  async resetMlp(model) {
    let response = await this.fetch.request(`/mlps/reset/${model.id}`);

    this.store.push(this.store.normalize(getRecordType(model), response.mlp));
    this.flashMessages.success('flash.resetMlp.success');

    return model;
  }

  async saveModel(model) {
    try {
      if (this.isModelInvalid(model)) {
        this.showErrors = true;
        return;
      }

      await this.recordLocks.lockStatus(model);

      if (!this.recordLocks.isLocked(model)) {
        const saveHooks = getRecordSettings(model, 'saveHooks');

        for (const hook of saveHooks?.before ?? []) {
          await hook(model);
        }

        await model.save();

        this.recordRelationships.markSaved(model);

        for (const hook of saveHooks?.after ?? []) {
          await hook(model);
        }

        const successMessage = this.intl.t('flash.saveModel.success', {
          displayHeadline: model.displayHeadline,
        });

        // TODO: Lock after save
        await this.recordLocks.lock(model);

        this.flashMessages.success(successMessage, {
          rawString: true,
        });

        return model;
      }
    } catch (error) {
      this.flashMessages.serverError(error);
      throw error;
    }
  }

  isModelInvalid(model) {
    let { validations } = model;

    if (validations.isTruelyInvalid) {
      this.flashMessages.error('flash.validations.error');

      validations.errors.forEach((error) => {
        this.flashMessages.error(error.message, {
          rawString: true,
        });
      });

      return true;
    }

    return false;
  }
}
